/**
 * 页面浏览量统计埋点工具
 * 根据设计文档实现的智能化埋点方案
 * 特点：前端只需传2个参数（moduleCode, pageCode），其他信息后端自动识别
 */

import request from './request' // 引入已配置的axios实例

/**
 * 页面浏览埋点（极简版）
 * @param {String} moduleCode - 模块编号，如 'M001'
 * @param {String} pageCode - 页面编号，如 'P001'
 * @param {Object} extraParams - 扩展参数（可选），如停留时长等
 */
export function trackPageView(moduleCode, pageCode, extraParams = {}) {
  // 参数校验
  if (!moduleCode || !pageCode) {
    console.warn('[Track] moduleCode and pageCode are required')
    return
  }

  // 只传必要参数，其他信息后端自动获取
  const data = {
    moduleCode,
    pageCode,
    extraParams: extraParams || {}
  }

  console.log('[Track] Sending pageview:', { moduleCode, pageCode })

  // 使用 request 发送（已自动携带token和headers）
  request.post('/api/track/pageview', data)
    .then((response) => {
      console.log('[Track] ✓ Page view tracked successfully:', moduleCode, pageCode, response)
    })
    .catch((error) => {
      // 记录错误但不影响业务
      console.warn('[Track] ✗ Track failed:', error.message || error)
      console.warn('[Track] Request data:', data)
    })
}

/**
 * 带停留时长的页面埋点
 * 在页面离开时调用，记录用户在页面的停留时间
 * @param {String} moduleCode - 模块编号
 * @param {String} pageCode - 页面编号
 * @param {Number} stayDuration - 停留时长（秒）
 */
export function trackPageViewWithDuration(moduleCode, pageCode, stayDuration) {
  trackPageView(moduleCode, pageCode, { stayDuration })
}

/**
 * 创建页面停留时长追踪器
 * 使用方式：在页面mounted时调用，在beforeUnmount时获取停留时长
 * @returns {Object} 返回包含 getDuration 方法的对象
 */
export function createDurationTracker() {
  const startTime = Date.now()
  
  return {
    /**
     * 获取停留时长（秒）
     */
    getDuration() {
      return Math.floor((Date.now() - startTime) / 1000)
    }
  }
}

/**
 * Vue 3 组合式API的埋点Hook
 * @param {String} moduleCode - 模块编号
 * @param {String} pageCode - 页面编号
 * @param {Boolean} trackDuration - 是否追踪停留时长，默认false
 */
export function usePageTrack(moduleCode, pageCode, trackDuration = false) {
  let tracker = null

  // 页面进入时埋点
  const onEnter = () => {
    trackPageView(moduleCode, pageCode)
    if (trackDuration) {
      tracker = createDurationTracker()
    }
  }

  // 页面离开时埋点（带停留时长）
  const onLeave = () => {
    if (trackDuration && tracker) {
      const duration = tracker.getDuration()
      trackPageViewWithDuration(moduleCode, pageCode, duration)
    }
  }

  return {
    onEnter,
    onLeave
  }
}

/**
 * 页面编码配置映射表
 * 根据路由路径自动匹配对应的模块和页面编码
 */
export const PAGE_CODE_MAP = {
  '/home': { moduleCode: 'M001', pageCode: 'P001', pageName: '首页' },
  
  // 用户认证模块
  '/login': { moduleCode: 'M002', pageCode: 'P001', pageName: '登录页' },
  '/register': { moduleCode: 'M002', pageCode: 'P002', pageName: '注册页' },
  
  // 资讯中心模块
  '/article': { moduleCode: 'M003', pageCode: 'P001', pageName: '资讯列表页' },
  '/information': { moduleCode: 'M003', pageCode: 'P002', pageName: '资讯详情页' },
  
  // 政策申报模块
  '/policy/talentPolicy': { moduleCode: 'M004', pageCode: 'P001', pageName: '政策申报列表' },
  '/policy/policyInfo': { moduleCode: 'M004', pageCode: 'P002', pageName: '政策详情页' },
  '/policyApply': { moduleCode: 'M004', pageCode: 'P003', pageName: '政策申报表单' },
  '/applyDetail': { moduleCode: 'M004', pageCode: 'P004', pageName: '申报详情查看' },
  
  // 人才认定模块
  '/identify/identifyList': { moduleCode: 'M005', pageCode: 'P001', pageName: '认定项目列表' },
  '/identify/identifyInfo': { moduleCode: 'M005', pageCode: 'P002', pageName: '认定项目详情' },
  '/identify/commitment': { moduleCode: 'M005', pageCode: 'P003', pageName: '承诺书页面' },
  '/identify/form': { moduleCode: 'M005', pageCode: 'P004', pageName: '认定申请表单' },
  '/identify/note': { moduleCode: 'M005', pageCode: 'P005', pageName: '申请须知' },
  '/identify/captcha': { moduleCode: 'M005', pageCode: 'P006', pageName: '证书核验' },
  
  // 人才机遇模块
  '/opportunity/list': { moduleCode: 'M006', pageCode: 'P001', pageName: '机遇列表页' },
  '/opportunity/jobInfo': { moduleCode: 'M006', pageCode: 'P002', pageName: '职位详情页' },
  
  // 企业管理模块
  '/entManage': { moduleCode: 'M007', pageCode: 'P001', pageName: '企业管理首页' },
  
  // 政策一览模块
  '/policyOverview': { moduleCode: 'M008', pageCode: 'P001', pageName: '政策一览列表' },
  '/policyOverviewInfo': { moduleCode: 'M008', pageCode: 'P002', pageName: '政策一览详情' },
  '/policyContent': { moduleCode: 'M008', pageCode: 'P003', pageName: '政策内容详情' },
  
  // 人才服务模块
  '/serviceHome': { moduleCode: 'M009', pageCode: 'P001', pageName: '人才服务首页' },
  '/health': { moduleCode: 'M009', pageCode: 'P002', pageName: '健康服务' },
  '/fprCard': { moduleCode: 'M009', pageCode: 'P003', pageName: 'FPR卡服务' },
  '/storage': { moduleCode: 'M009', pageCode: 'P004', pageName: '存储服务' },
  
  // 账号管理模块
  '/account': { moduleCode: 'M010', pageCode: 'P001', pageName: '账号管理页' },
  
  // 档案模块
  '/dossier': { moduleCode: 'M011', pageCode: 'P001', pageName: '档案管理页' },
  
  // 通知公告模块
  '/notice': { moduleCode: 'M012', pageCode: 'P001', pageName: '通知列表页' }
}

/**
 * 根据路由路径自动获取页面编码
 * @param {String} path - 路由路径
 * @returns {Object|null} 返回 { moduleCode, pageCode } 或 null
 */
export function getPageCodeByPath(path) {
  return PAGE_CODE_MAP[path] || null
}

export default {
  trackPageView,
  trackPageViewWithDuration,
  createDurationTracker,
  usePageTrack,
  getPageCodeByPath,
  PAGE_CODE_MAP
}
